import CheckIcon from "@mui/icons-material/Check";
import DescriptionIcon from "@mui/icons-material/Description";
import LockResetIcon from "@mui/icons-material/LockReset";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Typography,
} from "@mui/material";
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as EditIcon } from "../../../common/icons/editSmall.svg";
import { ReactComponent as DisableIcon } from "../../../common/icons/lock.svg";
import { Talent } from "../../talents/talentAPI";
import { TalentStatus } from "../../talents/types/common";
import isEmpty from "../../../common/helpers/isEmpty";

export enum MenuItemKey {
  ENABLE = "enable",
  DISABLE = "disable",
  EDIT_EMAIL = "editEmail",
  REDACTION_PLAN = "redactionPlan",
  RESET_PASSWORD = "resetPassword",
}

export type MenuItem = {
  key: MenuItemKey;
  icon: ReactElement;
  color?: string;
};

type Action = (talent: Talent | null) => void;

type Props = {
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
  talent: Talent | null;
  onExited: () => void;
  menuActions: {
    enable: Action;
    disable: Action;
    editEmail: Action;
    redactionPlan: Action;
    resetPassword: Action;
  };
};

const baseMenuItems: MenuItem[] = [
  {
    key: MenuItemKey.EDIT_EMAIL,
    icon: <EditIcon />,
  },
];

const resetPasswordItem: MenuItem = {
  key: MenuItemKey.RESET_PASSWORD,
  icon: <LockResetIcon sx={{ width: 22, height: 22, margin: "-3px" }} />,
};

const redactionPlanItem: MenuItem = {
  key: MenuItemKey.REDACTION_PLAN,
  icon: <DescriptionIcon />,
};

const statusMenuItems: MenuItem[] = [
  {
    key: MenuItemKey.DISABLE,
    icon: <DisableIcon />,
    color: "error.dark",
  },
  {
    key: MenuItemKey.ENABLE,
    icon: <CheckIcon />,
  },
];

const getMenuItems = (talent: Talent | null): MenuItem[] => {
  const status = talent?.account?.status;

  if (status === TalentStatus.DEACTIVATED) return [redactionPlanItem];

  if (status === TalentStatus.DISABLED) {
    return [
      ...baseMenuItems,
      redactionPlanItem,
      ...statusMenuItems.filter((item) => item.key === MenuItemKey.ENABLE),
    ];
  }

  if (
    status === TalentStatus.INCOMPLETE ||
    status === TalentStatus.NEW ||
    status === TalentStatus.INTERVIEW ||
    status === TalentStatus.INTERVIEW_PENDING ||
    status === TalentStatus.ACTIVE ||
    status === TalentStatus.LATER ||
    status === TalentStatus.EMPLOYED_ELSEWHERE ||
    status === TalentStatus.NOT_EMPLOYED
  ) {
    return [
      ...baseMenuItems,
      resetPasswordItem,
      ...statusMenuItems.filter((item) => item.key === MenuItemKey.DISABLE),
    ];
  }

  return [];
};

export const TalentsActionMenu = ({
  open,
  anchorEl,
  onClose,
  talent,
  onExited,
  menuActions,
}: Props) => {
  const { t } = useTranslation();

  const handleClick = (key: MenuItemKey) => {
    menuActions[key](talent);
    onClose();
  };

  const menuItems = getMenuItems(talent);

  return !isEmpty(menuItems) ? (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      TransitionProps={{ onExited }}
    >
      <List dense disablePadding>
        {menuItems.map((menuItem) => (
          <ListItem key={menuItem.key} disablePadding>
            <ListItemButton
              sx={{
                paddingLeft: "13px",
                paddingRight: "13px",
              }}
              onClick={() => handleClick(menuItem.key)}
            >
              <ListItemIcon sx={{ minWidth: "13px", marginRight: "13px" }}>
                {menuItem.icon}
              </ListItemIcon>
              <ListItemText>
                <Typography {...(menuItem?.color && { color: menuItem.color })}>
                  {t(`settings:${menuItem.key}`)}
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Menu>
  ) : (
    <></>
  );
};
